/**
 * Type definitions for Massnahmen (Measures/Actions) data
 */


/**
 * Funnel categories for categorized measures and recommendations
 */
export const FUNNEL_CATEGORIES = ['Awareness', 'Consideration', 'Conversion', 'Loyalty'] as const;
export type FunnelCategory = typeof FUNNEL_CATEGORIES[number];

/**
 * Single step-by-step guide entry
 */
export interface SchrittLeitfaden {
  Titel_Leitfaden: string;
  Kurzbeschreibung_Leitfaden: string;
}

/**
 * Single Massnahme (measure/action) table entry
 */
export interface MassnahmeTableEntry {
  Titel: string;
  Rang_in_Phase?: number;
  Phase?: FunnelCategory;
  Journey_Schritt?: number;
  Kategorie?: string;
  Format?: string;
  Konkretes_Format?: string;
  Interaktionstyp?: string;
  Geschaetzte_Kosten: string | number;
  Return_on_Investment: string | number;
  Anzahl_Neukunden: string | number;
  Reach?: number;
  Impact?: number;
  Confidence?: number;
  Effort?: number;
  RICE?: number;
  Kategorie_Effizienz?: number;
  Gewichteter_Score?: number;
  Verstaendliche_Beschreibung?: string;
  Konkrete_Beispiele?: string[];
  Anreiz_fuer_Dritte?: string;
  Umsetzungsziel?: string;
  Vorbereitung?: string;
  Schritt_fuer_Schritt?: SchrittLeitfaden[] | string[];
  Beteiligte_Rollen?: string[];
  Naechster_Phase_Uebergang?: string;
}

/**
 * Main Massnahmen data structure from API
 */
export interface MassnahmenData {
  Massnahmen_Tabelle: Record<FunnelCategory, MassnahmeTableEntry[]>;
  Massnahmen_Zusammenfassung: string;
}

/**
 * Combined data structure for card and overlay
 */
export interface MassnahmenFullData {
  main: MassnahmenData;
}

/**
 * Flatten categorized Massnahmen_Tabelle into a flat array (for card backward compat)
 */
export function flattenMassnahmenTabelle(
  table: Record<FunnelCategory, MassnahmeTableEntry[]> | MassnahmeTableEntry[] | undefined,
): MassnahmeTableEntry[] {
  if (!table) return [];
  // Handle legacy flat array format
  if (Array.isArray(table)) return table;
  return FUNNEL_CATEGORIES.flatMap(cat => table[cat] || []);
}

/**
 * Normalize Schritt_fuer_Schritt: handles both legacy string[] and new SchrittLeitfaden[] format
 */
export function normalizeSchritte(
  steps: SchrittLeitfaden[] | string[] | undefined,
): SchrittLeitfaden[] {
  if (!steps || steps.length === 0) return [];
  if (typeof steps[0] === 'string') {
    return (steps as string[]).filter(s => s.trim()).map(s => ({
      Titel_Leitfaden: s,
      Kurzbeschreibung_Leitfaden: '',
    }));
  }
  return (steps as SchrittLeitfaden[]).filter(s => s.Titel_Leitfaden?.trim());
}

/**
 * Normalize a single entry from legacy format (Name/Prio/Beschreibung) to new format (Titel/Rang_in_Phase)
 */
function normalizeEntry(raw: Record<string, unknown>): MassnahmeTableEntry {
  const entry = { ...raw } as Record<string, unknown>;
  // Rename legacy fields
  if ('Name' in entry && !('Titel' in entry)) {
    entry.Titel = entry.Name;
    delete entry.Name;
  }
  if ('Prio' in entry && !('Rang_in_Phase' in entry)) {
    entry.Rang_in_Phase = entry.Prio;
    delete entry.Prio;
  }
  // Drop removed field
  delete entry.Beschreibung;
  return entry as unknown as MassnahmeTableEntry;
}

/**
 * Parse raw JSON string from API into structured Massnahmen data
 */
export function parseMassnahmenData(jsonString: string): MassnahmenFullData | null {
  try {
    let data = jsonString;

    // If the string is wrapped in quotes, try to parse it first
    if (typeof data === 'string' && (data.startsWith('"') || data.startsWith("'"))) {
      try {
        data = JSON.parse(data);
      } catch {
        // If it fails, continue with original string
      }
    }

    const parsed = typeof data === 'string' ? JSON.parse(data) : data;

    // Handle case where it's an array
    const mainData = Array.isArray(parsed) ? parsed[0] : parsed;

    if (!mainData) {
      return null;
    }

    // New flat format: { B2C_Customer_Journey_Umsetzung: [...] }
    if (mainData.B2C_Customer_Journey_Umsetzung && Array.isArray(mainData.B2C_Customer_Journey_Umsetzung)) {
      const flatEntries: MassnahmeTableEntry[] = mainData.B2C_Customer_Journey_Umsetzung.map(
        (raw: Record<string, unknown>) => normalizeEntry(raw),
      );
      // Group by Phase into categorized table
      const table: Record<FunnelCategory, MassnahmeTableEntry[]> = {
        Awareness: [], Consideration: [], Conversion: [], Loyalty: [],
      };
      for (const entry of flatEntries) {
        const phase = entry.Phase as FunnelCategory | undefined;
        if (phase && phase in table) {
          table[phase].push(entry);
        }
      }
      return {
        main: {
          Massnahmen_Tabelle: table,
          Massnahmen_Zusammenfassung: mainData.Massnahmen_Zusammenfassung || '',
        },
      };
    }

    // B2C categorized format: { Customer_Journey_Map: { Awareness: [...], ... } }
    if (mainData.Customer_Journey_Map && typeof mainData.Customer_Journey_Map === 'object' && !Array.isArray(mainData.Customer_Journey_Map)) {
      const rawTable = mainData.Customer_Journey_Map;
      const table: Record<FunnelCategory, MassnahmeTableEntry[]> = {
        Awareness: [], Consideration: [], Conversion: [], Loyalty: [],
      };
      for (const cat of FUNNEL_CATEGORIES) {
        if (Array.isArray(rawTable[cat])) {
          table[cat] = rawTable[cat].map((raw: Record<string, unknown>) => normalizeEntry(raw));
        }
      }
      return {
        main: {
          Massnahmen_Tabelle: table,
          Massnahmen_Zusammenfassung: mainData.Customer_Journey_Zusammenfassung || mainData.Massnahmen_Zusammenfassung || '',
        },
      };
    }

    // Legacy categorized format: { Massnahmen_Tabelle: { Awareness: [...], ... } }
    if (mainData.Massnahmen_Tabelle) {
      const rawTable = mainData.Massnahmen_Tabelle;
      if (typeof rawTable === 'object' && !Array.isArray(rawTable)) {
        const table: Record<FunnelCategory, MassnahmeTableEntry[]> = {
          Awareness: [], Consideration: [], Conversion: [], Loyalty: [],
        };
        for (const cat of FUNNEL_CATEGORIES) {
          if (Array.isArray(rawTable[cat])) {
            table[cat] = rawTable[cat].map((raw: Record<string, unknown>) => normalizeEntry(raw));
          }
        }
        return {
          main: {
            Massnahmen_Tabelle: table,
            Massnahmen_Zusammenfassung: mainData.Massnahmen_Zusammenfassung || '',
          },
        };
      }
    }

    return {
      main: mainData as MassnahmenData,
    };
  } catch {
    return null;
  }
}

/**
 * Generate mock data for development
 */
export function getMockMassnahmenData(): MassnahmenFullData {
  const mockData: MassnahmenData = {
    Massnahmen_Tabelle: {
      Awareness: [
        {
          Titel: 'LinkedIn Kampagne',

          Geschaetzte_Kosten: '2.500 €/Monat',
          Return_on_Investment: '8.5',
          Anzahl_Neukunden: '15-20',
          Rang_in_Phase: 1,
          Interaktionstyp: 'virtuell',
          Konkretes_Format: 'Social Media Werbung',
          Verstaendliche_Beschreibung: 'LinkedIn ist die wichtigste Plattform für B2B-Marketing. Mit gezielten Werbekampagnen erreichen Sie Entscheider in Ihrer Zielbranche direkt. Die Plattform ermöglicht präzises Targeting nach Jobtitel, Branche und Unternehmensgröße.',
          Konkrete_Beispiele: ['Sponsored Content mit Brancheninsights', 'InMail-Kampagne an Entscheider', 'LinkedIn Lead Gen Forms für Whitepaper-Downloads'],
          Anreiz_fuer_Dritte: '',
          Umsetzungsziel: 'Monatlich 50 qualifizierte Leads aus der Zielbranche über LinkedIn generieren.',
          Vorbereitung: 'LinkedIn Business Account einrichten, Zielgruppen-Targeting definieren und Kampagnen-Budget freigeben.',
          Schritt_fuer_Schritt: [
            { Titel_Leitfaden: 'Zielgruppen-Personas definieren', Kurzbeschreibung_Leitfaden: 'Jobtitel, Branche und Region auf LinkedIn festlegen' },
            { Titel_Leitfaden: 'Kampagnen-Content erstellen', Kurzbeschreibung_Leitfaden: 'Sponsored Posts und InMail-Vorlagen vorbereiten' },
            { Titel_Leitfaden: 'A/B-Tests aufsetzen', Kurzbeschreibung_Leitfaden: 'Verschiedene Anzeigenvarianten gegeneinander testen' },
            { Titel_Leitfaden: 'Monitoring & Optimierung', Kurzbeschreibung_Leitfaden: 'Wöchentliche Auswertung und Kampagnen-Performance optimieren' },
          ],
          Beteiligte_Rollen: ['Marketing Manager', 'Content Creator', 'Vertriebsleitung'],
        },
        {
          Titel: 'Content Marketing',
          Geschaetzte_Kosten: '1.500 €/Monat',
          Return_on_Investment: '6.8',
          Anzahl_Neukunden: '8-12',
          Rang_in_Phase: 2,
          Interaktionstyp: 'virtuell',
          Konkretes_Format: 'Blog & Whitepaper',
          Verstaendliche_Beschreibung: 'Durch hochwertige Fachartikel und Whitepaper positionieren Sie sich als Experte in Ihrer Branche. Potenzielle Kunden finden Ihre Inhalte über Suchmaschinen und bauen Vertrauen auf, bevor sie Kontakt aufnehmen.',
          Konkrete_Beispiele: ['Monatlicher Blogbeitrag zu Branchentrends', 'Whitepaper mit konkreten Handlungsempfehlungen', 'Infografiken für Social Media Sharing'],
          Anreiz_fuer_Dritte: '',
        },
        {
          Titel: 'Social Media Branding',
          Geschaetzte_Kosten: '1.200 €/Monat',
          Return_on_Investment: '5.5',
          Anzahl_Neukunden: '6-10',
          Rang_in_Phase: 3,
          Interaktionstyp: 'virtuell',
          Konkretes_Format: 'Social Media Präsenz',
          Verstaendliche_Beschreibung: 'Eine konsistente und professionelle Präsenz auf Social Media stärkt Ihre Markenbekanntheit. Regelmäßige Posts, Stories und Interaktionen mit Ihrer Zielgruppe schaffen Vertrauen und Sichtbarkeit.',
          Konkrete_Beispiele: ['Regelmäßige Posts auf Instagram und LinkedIn', 'Behind-the-Scenes Content', 'Community Management und Interaktion'],
          Anreiz_fuer_Dritte: '',
        },
        {
          Titel: 'PR & Pressemitteilungen',
          Geschaetzte_Kosten: '2.000 €/Monat',
          Return_on_Investment: '4.2',
          Anzahl_Neukunden: '5-8',
          Rang_in_Phase: 4,
          Interaktionstyp: 'physisch',
          Konkretes_Format: 'Pressemitteilung',
          Verstaendliche_Beschreibung: 'Durch gezielte PR-Arbeit und Pressemitteilungen steigern Sie Ihre Sichtbarkeit in relevanten Medien. Journalisten und Redakteure greifen Ihre Themen auf und positionieren Sie als Branchenexperte.',
          Konkrete_Beispiele: ['Quartalsweise Pressemitteilungen zu Unternehmensnews', 'Gastbeiträge in Fachmagazinen', 'Pressekonferenz bei Produktneuheiten'],
          Anreiz_fuer_Dritte: 'Exklusive Vorab-Informationen und Interviews für Fachjournalisten.',
        },
        {
          Titel: 'Podcast-Sponsoring',
          Geschaetzte_Kosten: '1.800 €/Monat',
          Return_on_Investment: '5.0',
          Anzahl_Neukunden: '4-7',
          Rang_in_Phase: 5,
          Interaktionstyp: 'virtuell',
          Konkretes_Format: 'Audio-Sponsoring',
          Verstaendliche_Beschreibung: 'Podcasts erreichen Ihre Zielgruppe in einem aufmerksamen Moment. Durch Sponsoring relevanter Branchenpodcasts platzieren Sie Ihre Marke direkt im Ohr potenzieller Kunden.',
          Konkrete_Beispiele: ['Sponsoring eines Top-Branchenpodcasts', 'Eigener Gastauftritt im Podcast', 'Pre-Roll Werbung mit speziellem Angebot'],
          Anreiz_fuer_Dritte: 'Podcast-Hosts erhalten kostenlose Produkt-/Servicenutzung für authentische Empfehlung.',
        },
      ],
      Consideration: [
        {
          Titel: 'Webinar-Reihe',
          Geschaetzte_Kosten: '1.000 €/Monat',
          Return_on_Investment: '7.2',
          Anzahl_Neukunden: '10-15',
          Rang_in_Phase: 1,
          Interaktionstyp: 'virtuell',
          Konkretes_Format: 'Online-Webinar',
          Verstaendliche_Beschreibung: 'Webinare bieten die Möglichkeit, Ihr Fachwissen live zu demonstrieren und direkt mit Interessenten zu interagieren. Teilnehmer lernen Ihr Unternehmen und Ihre Expertise kennen, bevor sie eine Kaufentscheidung treffen.',
          Konkrete_Beispiele: ['Monatliches Experten-Webinar zu Branchenthemen', 'Q&A-Session mit Live-Demonstration', 'Aufzeichnung als On-Demand-Content bereitstellen'],
          Anreiz_fuer_Dritte: '',
        },
        {
          Titel: 'Case Studies erstellen',
          Geschaetzte_Kosten: '800 €/Monat',
          Return_on_Investment: '6.5',
          Anzahl_Neukunden: '5-8',
          Rang_in_Phase: 2,
          Interaktionstyp: 'virtuell',
          Konkretes_Format: 'Dokument / PDF',
          Verstaendliche_Beschreibung: 'Erfolgsgeschichten bestehender Kunden sind einer der stärksten Vertrauensbeweise. Potenzielle Kunden sehen konkret, wie Ihr Produkt oder Service anderen Unternehmen geholfen hat, und können sich mit den Ergebnissen identifizieren.',
          Konkrete_Beispiele: ['Detaillierte Case Study mit messbaren Ergebnissen', 'Video-Testimonial eines zufriedenen Kunden', 'Vorher-Nachher-Vergleich mit konkreten Zahlen'],
          Anreiz_fuer_Dritte: 'Bestehende Kunden erhalten Rabatte oder exklusive Services als Dank für ihre Mitwirkung.',
        },
        {
          Titel: 'E-Mail Marketing Automation',
          Geschaetzte_Kosten: '800 €/Monat',
          Return_on_Investment: '7.0',
          Anzahl_Neukunden: '10-15',
          Rang_in_Phase: 3,
          Interaktionstyp: 'virtuell',
          Konkretes_Format: 'E-Mail-Sequenz',
          Verstaendliche_Beschreibung: 'Automatisierte E-Mail-Sequenzen begleiten Interessenten systematisch durch den Entscheidungsprozess. Jede E-Mail liefert relevante Informationen zum richtigen Zeitpunkt und baut schrittweise Vertrauen auf.',
          Konkrete_Beispiele: ['5-teilige Welcome-Sequenz für neue Leads', 'Automatische Follow-up E-Mails nach Webinar-Teilnahme', 'Personalisierte Produktempfehlungen basierend auf Interesse'],
          Anreiz_fuer_Dritte: '',
        },
        {
          Titel: 'Bewertungsportale',
          Geschaetzte_Kosten: '600 €/Monat',
          Return_on_Investment: '5.8',
          Anzahl_Neukunden: '7-10',
          Rang_in_Phase: 4,
          Interaktionstyp: 'virtuell',
          Konkretes_Format: 'Bewertungsportal',
          Verstaendliche_Beschreibung: 'Viele Kunden prüfen Bewertungen, bevor sie sich für einen Anbieter entscheiden. Eine aktive Präsenz auf Bewertungsplattformen mit positiven Rezensionen stärkt das Vertrauen und verbessert Ihre Sichtbarkeit.',
          Konkrete_Beispiele: ['Google-Bewertungen aktiv sammeln und pflegen', 'Profil auf branchenspezifischen Portalen optimieren', 'Professionell auf Bewertungen reagieren'],
          Anreiz_fuer_Dritte: '',
        },
        {
          Titel: 'Kostenlose Beratungsgespräche',
          Geschaetzte_Kosten: '500 €/Monat',
          Return_on_Investment: '8.0',
          Anzahl_Neukunden: '8-12',
          Rang_in_Phase: 5,
          Interaktionstyp: 'physisch',
          Konkretes_Format: 'Persönliches Gespräch',
          Verstaendliche_Beschreibung: 'Ein kostenloses Erstberatungsgespräch gibt Interessenten die Möglichkeit, Sie persönlich kennenzulernen und Vertrauen aufzubauen. Sie können individuell auf Bedürfnisse eingehen und Ihre Kompetenz direkt unter Beweis stellen.',
          Konkrete_Beispiele: ['30-Minuten Erstberatung per Video oder vor Ort', 'Individuelle Bedarfsanalyse mit konkretem Feedback', 'Unverbindliches Angebot nach dem Gespräch'],
          Anreiz_fuer_Dritte: '',
        },
      ],
      Conversion: [
        {
          Titel: 'Google Ads Kampagne',
          Geschaetzte_Kosten: '1.800 €/Monat',
          Return_on_Investment: '6.0',
          Anzahl_Neukunden: '12-18',
          Rang_in_Phase: 1,
          Interaktionstyp: 'virtuell',
          Konkretes_Format: 'Suchmaschinenwerbung',
          Verstaendliche_Beschreibung: 'Wenn potenzielle Kunden aktiv nach Ihren Produkten oder Dienstleistungen suchen, erscheinen Ihre Anzeigen ganz oben in den Suchergebnissen. Sie erreichen Menschen genau in dem Moment, in dem sie kaufbereit sind.',
          Konkrete_Beispiele: ['Search-Ads für Top-Keywords in Ihrer Region', 'Lokale Anzeigenerweiterungen mit Standort und Telefon', 'Remarketing-Listen für Suchkampagnen (RLSA)'],
          Anreiz_fuer_Dritte: '',
        },
        {
          Titel: 'Landing Page Optimierung',
          Geschaetzte_Kosten: '1.200 €/Monat',
          Return_on_Investment: '7.5',
          Anzahl_Neukunden: '10-14',
          Rang_in_Phase: 2,
          Interaktionstyp: 'virtuell',
          Konkretes_Format: 'Webseite',
          Verstaendliche_Beschreibung: 'Ihre Landing Pages sind der entscheidende Moment, in dem Besucher zu Kunden werden. Durch systematische A/B-Tests und Optimierungen maximieren Sie die Conversion-Rate und senken gleichzeitig die Kosten pro Akquise.',
          Konkrete_Beispiele: ['A/B-Test verschiedener Call-to-Action Formulierungen', 'Optimierung der Formularfelder für höhere Abschlussrate', 'Vertrauenselemente wie Kundenstimmen prominent platzieren'],
          Anreiz_fuer_Dritte: '',
        },
        {
          Titel: 'Retargeting-Kampagnen',
          Geschaetzte_Kosten: '900 €/Monat',
          Return_on_Investment: '6.8',
          Anzahl_Neukunden: '8-12',
          Rang_in_Phase: 3,
          Interaktionstyp: 'virtuell',
          Konkretes_Format: 'Display-Werbung',
          Verstaendliche_Beschreibung: 'Die meisten Website-Besucher kaufen nicht beim ersten Besuch. Retargeting-Kampagnen erinnern diese Besucher an Ihr Angebot und bringen sie zurück auf Ihre Seite, wenn sie bereit sind zu kaufen.',
          Konkrete_Beispiele: ['Display-Ads für Website-Besucher der letzten 30 Tage', 'Dynamische Produktanzeigen basierend auf besuchten Seiten', 'Social Media Retargeting auf Facebook und Instagram'],
          Anreiz_fuer_Dritte: '',
        },
        {
          Titel: 'Vor-Ort Beratung',
          Geschaetzte_Kosten: '1.500 €/Monat',
          Return_on_Investment: '5.5',
          Anzahl_Neukunden: '15-20',
          Rang_in_Phase: 4,
          Interaktionstyp: 'physisch',
          Konkretes_Format: 'Persönliches Treffen',
          Verstaendliche_Beschreibung: 'Ein persönliches Treffen vor Ort schafft Nähe und Vertrauen. Sie können individuell auf die Situation des Kunden eingehen, seine Räumlichkeiten und Prozesse kennenlernen und maßgeschneiderte Lösungen präsentieren.',
          Konkrete_Beispiele: ['Individuelle Vor-Ort-Beratung mit Bedarfsanalyse', 'Produktdemonstration in der Kundenumgebung', 'Gemeinsamer Workshop zur Lösungsfindung'],
          Anreiz_fuer_Dritte: '',
        },
        {
          Titel: 'Live-Demo Angebot',
          Geschaetzte_Kosten: '700 €/Monat',
          Return_on_Investment: '8.2',
          Anzahl_Neukunden: '6-10',
          Rang_in_Phase: 5,
          Interaktionstyp: 'physisch',
          Konkretes_Format: 'Persönliche Demo',
          Verstaendliche_Beschreibung: 'Eine individuelle Live-Demo zeigt qualifizierten Interessenten genau, wie Ihr Produkt oder Service ihre konkreten Probleme löst. Die persönliche Demonstration überzeugt oft mehr als jede Broschüre.',
          Konkrete_Beispiele: ['30-Minuten Live-Demo mit individuellem Use Case', 'Screen-Sharing Session mit persönlicher Konfiguration', 'Testaccount mit begleiteter Einführung'],
          Anreiz_fuer_Dritte: '',
        },
      ],
      Loyalty: [
        {
          Titel: 'Kundenbindungsprogramm',
          Geschaetzte_Kosten: '1.000 €/Monat',
          Return_on_Investment: '9.0',
          Anzahl_Neukunden: '3-5',
          Rang_in_Phase: 1,
          Interaktionstyp: 'physisch',
          Konkretes_Format: 'Treueprogramm',
          Verstaendliche_Beschreibung: 'Ein strukturiertes Treueprogramm belohnt Ihre Bestandskunden für ihre Loyalität und motiviert sie zu weiteren Käufen. Exklusive Vorteile und Rabatte stärken die emotionale Bindung an Ihr Unternehmen.',
          Konkrete_Beispiele: ['Stufenbasiertes Treueprogramm mit wachsenden Vorteilen', 'Exklusive Events für Premium-Kunden', 'Geburtstagsrabatte und persönliche Überraschungen'],
          Anreiz_fuer_Dritte: '',
        },
        {
          Titel: 'Lokale Netzwerkveranstaltungen',
          Geschaetzte_Kosten: '500 €/Monat',
          Return_on_Investment: '5.5',
          Anzahl_Neukunden: '3-5',
          Rang_in_Phase: 2,
          Interaktionstyp: 'physisch',
          Konkretes_Format: 'Networking-Event',
          Verstaendliche_Beschreibung: 'Regionale Netzwerkveranstaltungen bieten die Möglichkeit, bestehende Kundenbeziehungen zu vertiefen und gleichzeitig neue Kontakte zu knüpfen. Der persönliche Austausch stärkt die Bindung nachhaltig.',
          Konkrete_Beispiele: ['Eigenes Kunden-Frühstück quartalsweise', 'Teilnahme an regionalen Branchentreffen', 'After-Work-Events für Bestandskunden und Interessenten'],
          Anreiz_fuer_Dritte: '',
        },
        {
          Titel: 'Kunden-Newsletter',
          Geschaetzte_Kosten: '400 €/Monat',
          Return_on_Investment: '6.0',
          Anzahl_Neukunden: '2-4',
          Rang_in_Phase: 3,
          Interaktionstyp: 'virtuell',
          Konkretes_Format: 'Newsletter',
          Verstaendliche_Beschreibung: 'Ein exklusiver Newsletter hält Ihre Bestandskunden regelmäßig informiert und liefert Mehrwert. So bleiben Sie im Bewusstsein Ihrer Kunden und positionieren sich als verlässlicher Partner.',
          Konkrete_Beispiele: ['Monatlicher Newsletter mit Brancheninsights', 'Exklusive Tipps und Best Practices für Kunden', 'Frühzeitiger Zugang zu neuen Produkten und Features'],
          Anreiz_fuer_Dritte: '',
        },
        {
          Titel: 'Empfehlungsprogramm',
          Geschaetzte_Kosten: '800 €/Monat',
          Return_on_Investment: '8.5',
          Anzahl_Neukunden: '8-12',
          Rang_in_Phase: 4,
          Interaktionstyp: 'virtuell',
          Konkretes_Format: 'Empfehlungssystem',
          Verstaendliche_Beschreibung: 'Zufriedene Kunden sind Ihre besten Verkäufer. Ein strukturiertes Empfehlungsprogramm motiviert Bestandskunden, Sie aktiv weiterzuempfehlen, und belohnt sie dafür mit attraktiven Prämien.',
          Konkrete_Beispiele: ['Geldprämie für jede erfolgreiche Empfehlung', 'Rabattcode zum Teilen mit Geschäftspartnern', 'Exklusives Upgrade bei 3 erfolgreichen Empfehlungen'],
          Anreiz_fuer_Dritte: 'Geworbene Neukunden erhalten einen Willkommensrabatt von 10% auf die erste Bestellung.',
        },
        {
          Titel: 'Kundenzufriedenheitsumfragen',
          Geschaetzte_Kosten: '300 €/Monat',
          Return_on_Investment: '4.0',
          Anzahl_Neukunden: '1-3',
          Rang_in_Phase: 5,
          Interaktionstyp: 'virtuell',
          Konkretes_Format: 'Online-Umfrage',
          Verstaendliche_Beschreibung: 'Regelmäßige Zufriedenheitsumfragen zeigen Ihren Kunden, dass Ihnen ihre Meinung wichtig ist. Gleichzeitig erkennen Sie Probleme frühzeitig und können proaktiv gegensteuern, bevor Kunden abwandern.',
          Konkrete_Beispiele: ['Quartalsweise NPS-Umfrage per E-Mail', 'Feedback-Formular nach jedem Servicekontakt', 'Jährliche ausführliche Kundenzufriedenheitsstudie'],
          Anreiz_fuer_Dritte: '',
        },
      ],
    },
    Massnahmen_Zusammenfassung: 'Basierend auf der Analyse empfehlen wir eine Kombination aus LinkedIn-Marketing und E-Mail-Automation als primäre Massnahmen. LinkedIn bietet den höchsten ROI bei moderaten Kosten, während E-Mail-Marketing eine kosteneffiziente Ergänzung darstellt. Content Marketing sollte langfristig aufgebaut werden, um die Markenpositionierung zu stärken.',
  };

  return {
    main: mockData,
  };
}
